import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import {
  Hash,
  CalendarClock,
  MapPin,
  Users,
  Target,
  Link2,
  Video,
  Trash2,
  Calendar,
  Mail,
  Umbrella,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { useUIStore } from '@/core/stores/ui.store';
import { useToast } from '@/shared/hooks/useToast';
import { ModalityPicker } from './ModalityPicker';
import { AttendeesPicker } from './AttendeesPicker';
import { EventDatePicker } from './EventDatePicker';
import { useUpdateCalendarEvent } from '../hooks/useUpdateCalendarEvent';
import { toCalendarEventDateInput } from '../utils/calendar-event-date-input';
import type {
  CalendarEvent,
  CalendarEventAttendee,
  CalendarEventModality,
  CalendarSource,
  UpdateCalendarEventPayload,
} from '../types';

interface EventFichaProps {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete?: (event: CalendarEvent) => void;
}

const SOURCE_ICONS: Partial<Record<CalendarSource, typeof Calendar>> = {
  google: Calendar,
  microsoft: Mail,
  khronox: Umbrella,
};

function readString(metadata: Record<string, unknown> | undefined, key: string): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readModality(metadata: Record<string, unknown> | undefined): CalendarEventModality | null {
  const value = metadata?.modality;
  return value === 'virtual' || value === 'in-person' || value === 'hybrid' ? value : null;
}

function readAttendees(metadata: Record<string, unknown> | undefined): CalendarEventAttendee[] {
  const value = metadata?.attendees;
  return Array.isArray(value) ? (value as CalendarEventAttendee[]) : [];
}

/** Mirrors Tasks' `MetadataRow` — icon + "Label:" + inline control, one line. */
function EventMetadataRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{label}:</span>
      {children}
    </div>
  );
}

/** Click-to-edit single-line text, matching `TaskTitleEditor`'s commit-on-blur pattern. */
function InlineText({
  value,
  placeholder,
  onCommit,
  className,
  testId,
}: {
  value: string;
  placeholder: string;
  onCommit: (next: string) => void;
  className?: string;
  testId?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const originalRef = useRef(value);

  function startEdit() {
    originalRef.current = value;
    setDraft(value);
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== originalRef.current) onCommit(trimmed);
  }

  function cancel() {
    setEditing(false);
    setDraft(originalRef.current);
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') cancel();
        }}
        data-testid={testId}
        className={className}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      data-testid={testId}
      className="cursor-text truncate text-left text-xs hover:opacity-80"
    >
      {value || <span className="text-muted-foreground">{placeholder}</span>}
    </button>
  );
}

export function EventFicha({ event, open, onOpenChange, onDelete }: EventFichaProps) {
  const { t, i18n } = useTranslation('calendar');
  const { dateFormat, timeFormat } = useDateFormat();
  const timezone = useUIStore((s) => s.timezone);
  const { toast } = useToast();
  const updateEvent = useUpdateCalendarEvent();

  const isInternal = event?.source === 'internal';
  const dateFnsLocale = i18n.language.startsWith('es') ? es : enUS;

  // Local optimistic mirror of the editable metadata — per-field mutations
  // update this local copy immediately so the UI doesn't wait for a refetch.
  // Reset synchronously during render when the ficha switches to a different
  // event (the React-endorsed "adjusting state on prop change" pattern —
  // NOT a useEffect, which would cause an extra render pass and trip the
  // set-state-in-effect lint rule).
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [modality, setModality] = useState<CalendarEventModality | null>(null);
  const [attendees, setAttendees] = useState<CalendarEventAttendee[]>([]);
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(new Date());
  const [end, setEnd] = useState(new Date());
  const [allDay, setAllDay] = useState(false);
  const [loadedEventId, setLoadedEventId] = useState<string | null>(null);

  if (event && event.id !== loadedEventId) {
    setLoadedEventId(event.id);
    setTitle(event.title);
    setLocation(readString(event.metadata, 'location') ?? '');
    setModality(readModality(event.metadata));
    setAttendees(readAttendees(event.metadata));
    setDescription(readString(event.metadata, 'description') ?? '');
    setStart(event.start);
    setEnd(event.end);
    setAllDay(event.allDay ?? false);
  }

  // modal={false} + manual scroll lock: same Radix workaround as TaskDialog
  // (radix-ui/primitives#1159) — the ModalityPicker/AttendeesPicker/EventDatePicker
  // Popovers nested inside this Dialog would otherwise lose mouse-wheel scrolling.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!event) return null;

  const SourceIcon = SOURCE_ICONS[event.source];
  const organizerEmail = readString(event.metadata, 'organizerEmail');
  const calendarName = readString(event.metadata, 'calendarName');
  const webLink = readString(event.metadata, 'webLink');
  const meetingUrl = readString(event.metadata, 'meetingUrl');
  const sourceLabel = t(`eventDetail.sources.${event.source}`, { defaultValue: event.source });

  const rangeLabel = allDay
    ? format(start, dateFormat, { locale: dateFnsLocale })
    : `${format(start, dateFormat, { locale: dateFnsLocale })} ${format(start, timeFormat, { locale: dateFnsLocale })} – ${format(end, timeFormat, { locale: dateFnsLocale })}`;

  function saveField(payload: UpdateCalendarEventPayload) {
    updateEvent.mutate({ eventId: event!.id, payload });
  }

  async function copyId() {
    await navigator.clipboard.writeText(event!.id);
    toast.success({ title: t('eventFicha.idCopiedToast') });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl sm:rounded-lg"
        showCloseButton={false}
        data-testid="calendar-event-ficha"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <DialogTitle asChild>
            <div className="min-w-0 flex-1">
              {isInternal ? (
                <InlineText
                  value={title}
                  placeholder={t('eventFicha.titlePlaceholder')}
                  onCommit={(next) => {
                    if (!next) return;
                    setTitle(next);
                    saveField({ title: next });
                  }}
                  className="h-auto border-0 px-0 text-base font-semibold shadow-none focus-visible:ring-0"
                  testId="calendar-ficha-title-input"
                />
              ) : (
                <h2 className="flex items-center gap-2 truncate text-base font-semibold">
                  {SourceIcon && (
                    <SourceIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">{event.title}</span>
                </h2>
              )}
            </div>
          </DialogTitle>

          <div className="flex items-center gap-0.5">
            {isInternal && onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onDelete(event)}
                    aria-label={t('eventDetail.delete')}
                    data-testid="calendar-ficha-delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('eventDetail.delete')}</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onOpenChange(false)}
                  aria-label={t('eventFicha.close')}
                  data-testid="calendar-ficha-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('eventFicha.close')}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
          <div className="flex items-center gap-1.5">
            <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={copyId}
                  aria-label={t('eventFicha.idCopyTooltip')}
                  data-testid="calendar-ficha-id"
                  className="group rounded-md border px-2 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground"
                >
                  <span className="group-hover:hidden">{event.id}</span>
                  <span className="hidden group-hover:inline">{t('eventFicha.idCopyTooltip')}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('eventFicha.idCopyTooltip')}</TooltipContent>
            </Tooltip>
          </div>

          <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
            <EventMetadataRow icon={CalendarClock} label={t('eventFicha.dateTimeLabel')}>
              {isInternal ? (
                <EventDatePicker
                  start={start}
                  end={end}
                  allDay={allDay}
                  dateFormat={dateFormat}
                  timeFormat={timeFormat}
                  onSave={(nextStart, nextEnd, nextAllDay) => {
                    setStart(nextStart);
                    setEnd(nextEnd);
                    setAllDay(nextAllDay);
                    saveField({
                      allDay: nextAllDay,
                      start: toCalendarEventDateInput(nextStart, nextAllDay, timezone),
                      end: toCalendarEventDateInput(nextEnd, nextAllDay, timezone),
                    });
                  }}
                />
              ) : (
                <span className="text-xs">{rangeLabel}</span>
              )}
            </EventMetadataRow>

            <EventMetadataRow icon={Target} label={t('eventFicha.modality.label')}>
              <ModalityPicker
                modality={modality}
                disabled={!isInternal}
                onSelect={(next) => {
                  setModality(next);
                  saveField({ modality: next });
                }}
              />
            </EventMetadataRow>

            <EventMetadataRow icon={Users} label={t('eventFicha.attendees.label')}>
              <AttendeesPicker
                attendees={attendees}
                disabled={!isInternal}
                onChange={(next) => {
                  setAttendees(next);
                  saveField({
                    attendeeUserIds: next
                      .map((a) => a.userId)
                      .filter((id): id is string => !!id)
                      .map(Number),
                  });
                }}
              />
            </EventMetadataRow>

            <EventMetadataRow icon={MapPin} label={t('eventFicha.locationLabel')}>
              {isInternal ? (
                <InlineText
                  value={location}
                  placeholder={t('eventFicha.locationPlaceholder')}
                  onCommit={(next) => {
                    setLocation(next);
                    saveField({ location: next || null });
                  }}
                  className="h-7 text-xs"
                  testId="calendar-ficha-location-input"
                />
              ) : (
                <span className="truncate text-xs">
                  {location || <span className="text-muted-foreground">—</span>}
                </span>
              )}
            </EventMetadataRow>
          </div>

          {calendarName && <p className="text-xs text-muted-foreground">{calendarName}</p>}

          {meetingUrl && (
            <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <Video className="h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{sourceLabel}</p>
                <p className="truncate text-xs text-muted-foreground">{meetingUrl}</p>
              </div>
              <Button asChild size="sm">
                <a href={meetingUrl} target="_blank" rel="noopener noreferrer">
                  {t('eventDetail.joinMeeting')}
                </a>
              </Button>
            </div>
          )}

          {webLink && !meetingUrl && (
            <a
              href={webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Link2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {t('eventDetail.openInProvider', { provider: sourceLabel })}
            </a>
          )}

          {organizerEmail && (
            <p className="text-sm text-muted-foreground">
              {t('eventFicha.organizedBy', { email: organizerEmail })}
            </p>
          )}

          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('eventFicha.descriptionLabel')}
            </p>
            {isInternal ? (
              // variant="plain" (not "comment") deliberately — the "comment" box
              // applies overflow-hidden, which clips floating popups. The fixed
              // toolbar already covers formatting, so the selection bubble/floating
              // menus are disabled outright rather than merely un-clipped.
              <div className="rounded-lg border border-input bg-background">
                <RichTextEditor
                  value={description}
                  onChange={(json) => {
                    setDescription(json);
                    saveField({ description: json || null });
                  }}
                  placeholder={t('eventFicha.descriptionPlaceholder')}
                  variant="plain"
                  enableFixedToolbar
                  enableBubbleMenu={false}
                  enableFloatingMenu={false}
                  enableSlashCommands
                  className="min-h-24 px-3 py-2"
                />
              </div>
            ) : description ? (
              <p className="whitespace-pre-wrap text-sm">{description}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('eventFicha.noDescription')}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
